type StateType = string;

export type EventDefinition = {
    event: string;
    payload?: any;
};

// This type generic will create a union of types matching events to their payloads
export type SendArgs<T extends { event: string }> = T extends unknown
    ? T extends { payload: infer P }
        ? [event: T['event'], payload: P]
        : [event: T['event']]
    : never;

// Distributes over the union so events without a payload contribute `never` instead of
// collapsing the whole union to `{}` (which is what a plain `TEvents['payload']` does).
export type EventPayload<T extends EventDefinition> = T extends { payload: infer P } ? P : never;

export interface StateDefinition<TState extends StateType, TEvents extends EventDefinition, TContext = undefined> {
    /** Called right before this state is entered. */
    pre?: (
        context: TContext,
        from: TState,
        send: (...args: SendArgs<TEvents>) => boolean,
        payload?: EventPayload<TEvents>
    ) => void;
    /** Called right when this state is entered. */
    onEnter?: (
        context: TContext,
        current: TState,
        send: (...args: SendArgs<TEvents>) => boolean,
        payload?: EventPayload<TEvents>
    ) => void;
    /** Called right after this state is exited. */
    post?: (
        context: TContext,
        to: TState,
        send: (...args: SendArgs<TEvents>) => boolean,
        payload?: EventPayload<TEvents>
    ) => void;
    /** Map of event name -> next state. */
    transitions: Partial<Record<TEvents['event'], TState>>;
}

export type StateMachineDefinition<
    TState extends StateType,
    TEvents extends EventDefinition,
    TContext = undefined
> = Record<TState, StateDefinition<TState, TEvents, TContext>>;

export class StateMachine<TState extends StateType, TEvents extends EventDefinition, TContext = undefined> {
    private current: TState;
    private started = false;
    private readonly _context: TContext;

    constructor(
        private readonly definition: StateMachineDefinition<TState, TEvents, TContext>,
        initialState: TState,
        ...[context]: TContext extends undefined ? [context?: TContext] : [context: TContext]
    ) {
        this._context = context as TContext;
        this.current = initialState;
    }

    get context(): TContext {
        return this._context;
    }

    get state(): TState {
        return this.current;
    }

    /**
     * Runs the initial state's `pre` handler. Construction is side-effect free so it's
     * safe to call from render; this must be called once from a committed effect instead,
     * since `pre` may have side effects (or call `send`) that shouldn't run for a render
     * that StrictMode or concurrent React discards before commit. Safe to call more than
     * once (e.g. StrictMode's mount/cleanup/mount) — only the first call takes effect.
     */
    start(): void {
        if (this.started) return;
        this.started = true;
        // This will call the pre hook (if any) for the initial state. Note that `this.current` will be the
        // initial state, when technically it should be undefined (because we aren't in a state yet).
        //
        // This is an intentional trade-off. If we passed undefined, then every pre hook ever written would
        // have to do an undefined check. However, a pre hook on the initial state will be a minority of use
        // cases, so we put the added complexity in the minority of use-cases instead of in the normal use.
        this.definition[this.current].pre?.(this.context, this.current, this.send.bind(this), undefined);
    }

    /** Process an event, transitioning to the next state if one is defined. Returns true if a transition occurred. */
    send(...args: SendArgs<TEvents>): boolean {
        const [event, payload] = args as [TEvents['event'], EventPayload<TEvents>?];
        const state = this.definition[this.current];
        const next = state.transitions[event];

        console.debug(
            `StateMachine: current state: ${this.current}, event: ${String(event)}, next state: ${String(next)}`
        );

        if (next === undefined) {
            return false;
        }

        state.post?.(this.context, next, this.send.bind(this), payload);
        this.definition[next].pre?.(this.context, this.current, this.send.bind(this), payload);
        this.current = next;
        this.definition[next].onEnter?.(this.context, this.current, this.send.bind(this), payload);

        return true;
    }
}

/**
 * Constructs a `StateMachine` and immediately calls `start()`. Use this for plain
 * JS/Node usage; the React hook handles this internally.
 */
export function createStateMachine<TState extends StateType, TEvents extends EventDefinition, TContext = undefined>(
    definition: StateMachineDefinition<TState, TEvents, TContext>,
    initialState: TState,
    ...[context]: TContext extends undefined ? [context?: TContext] : [context: TContext]
): StateMachine<TState, TEvents, TContext> {
    const machine = new StateMachine<TState, TEvents, TContext>(
        definition,
        initialState,
        ...([context] as TContext extends undefined ? [context?: TContext] : [context: TContext])
    );
    machine.start();
    return machine;
}
